import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

export const SectionLabel = ({ children, testId }) => (
  <p className="section-label" data-testid={testId}>
    {children}
  </p>
);

export const LinkedInInlineIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M6.94 8.5a1.56 1.56 0 1 1 0-3.12 1.56 1.56 0 0 1 0 3.12ZM5.63 18.37h2.62V9.77H5.63v8.6Zm4.26-8.6h2.51v1.17h.04c.35-.66 1.2-1.35 2.47-1.35 2.64 0 3.13 1.74 3.13 4v4.78h-2.62v-4.24c0-1.01-.02-2.31-1.41-2.31-1.41 0-1.63 1.1-1.63 2.24v4.31H9.89v-8.6Z"
      fill="currentColor"
    />
  </svg>
);

export const InsightsCard = ({ article, index, baseTestId }) => {
  const imgSrc = article.hero_image_url || article.image;
  const displayImg = imgSrc?.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${imgSrc}` : imgSrc;
  const dateStr = article.publish_date
    ? new Date(article.publish_date).toLocaleDateString("en-US", { year: "numeric", month: "long" })
    : article.date || "";
  return (
  <article
    className="insights-card"
    data-animate="fade-up"
    style={{ transitionDelay: `${index * 90}ms` }}
    data-testid={`${baseTestId}-card-${index + 1}`}
  >
    <Link
      to={`/insights/${article.slug}`}
      className="insights-card-image"
      data-testid={`${baseTestId}-card-image-link-${index + 1}`}
    >
      <img src={displayImg} alt={article.title} />
    </Link>
    <div className="insights-card-body">
      <div className="insight-meta">
        <span className="category-pill">{article.category}</span>
        <span>{dateStr}</span>
      </div>
      <h3>{article.title}</h3>
      <p>{article.excerpt}</p>
      <Link
        to={`/insights/${article.slug}`}
        className="read-article-link"
        data-testid={`${baseTestId}-card-read-link-${index + 1}`}
      >
        <span>Read article</span>
        <span>&rarr;</span>
      </Link>
    </div>
  </article>
  );
};

export const PageHero = ({ label, headline, subtext, image, testIdPrefix }) => (
  <section
    className="page-hero"
    style={image ? { backgroundImage: `url('${image}')` } : undefined}
    data-testid={`${testIdPrefix}-hero-section`}
  >
    <div className="page-hero-overlay" />
    <div className="shell page-hero-content" data-animate="fade-up">
      {label && <SectionLabel testId={`${testIdPrefix}-hero-label`}>{label}</SectionLabel>}
      <h1 className="section-heading" data-testid={`${testIdPrefix}-hero-heading`}>
        {headline}
      </h1>
      {subtext && (
        <p className="section-body page-hero-subtext" data-testid={`${testIdPrefix}-hero-subtext`}>
          {subtext}
        </p>
      )}
    </div>
  </section>
);

export const Breadcrumbs = ({ items, testId }) => (
  <nav className="breadcrumbs" aria-label="Breadcrumb" data-testid={testId}>
    {items.map((item, i) => (
      <span key={i}>
        {i > 0 && <span className="breadcrumb-sep">&rarr;</span>}
        {item.path ? (
          <Link to={item.path}>{item.label}</Link>
        ) : (
          <span className="breadcrumb-current">{item.label}</span>
        )}
      </span>
    ))}
  </nav>
);

export const BackToTop = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 500);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <button
      className={`back-to-top ${visible ? 'visible' : ''}`}
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      aria-label="Back to top"
      data-testid="back-to-top-button"
    >
      &uarr;
    </button>
  );
};

export const useRevealAnimations = (watchKey) => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("in-view");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.16 }
    );
    const seen = new WeakSet();
    const hook = () => {
      document.querySelectorAll('[data-animate="fade-up"]').forEach((node) => {
        if (seen.has(node)) return;
        seen.add(node);
        node.classList.remove("in-view");
        observer.observe(node);
      });
    };
    hook();
    // Lazy-loaded routes mount after this effect runs; watch for new nodes.
    const mutations = new MutationObserver(hook);
    mutations.observe(document.body, { childList: true, subtree: true });
    return () => {
      mutations.disconnect();
      observer.disconnect();
    };
  }, [watchKey]);
};
